import { jsPDF } from "jspdf";
import "jspdf-autotable";

const safeStr = (v) => {
  if (v === null || v === undefined) return "";
  return String(v);
};

const parseMoneyLoose = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = safeStr(value).replace(/,/g, "").trim();
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const uniq = (arr) => [...new Set((Array.isArray(arr) ? arr : []).filter(Boolean))];

const formatDateMaybe = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeStr(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const monthKeyFrom = (value) => {
  if (!value) return null;
  const s = safeStr(value);
  if (s.length >= 7 && s[4] === "-" && s[6] !== undefined) return s.slice(0, 7);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const monthLabel = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

const ensureSpace = (doc, y, minRemaining = 64) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - minRemaining) {
    doc.addPage();
    return 40;
  }
  return y;
};

const sectionTitle = (doc, title, y) => {
  y = ensureSpace(doc, y, 72);
  doc.setFontSize(13);
  doc.setTextColor(15, 30, 53);
  doc.setFont(undefined, "bold");
  doc.text(title, 40, y);
  doc.setFont(undefined, "normal");
  return y + 14;
};

const addKeyValues = (doc, pairs, y, { leftX = 40, midX = 310, rowH = 14 } = {}) => {
  doc.setFontSize(10);
  doc.setTextColor(84, 110, 122);
  let cursorY = y;
  for (let idx = 0; idx < pairs.length; idx += 1) {
    cursorY = ensureSpace(doc, cursorY, 56);
    const left = pairs[idx];
    const right = pairs[idx + 1];
    doc.setFont(undefined, "bold");
    doc.text(`${safeStr(left?.k)}:`, leftX, cursorY);
    doc.setFont(undefined, "normal");
    doc.text(safeStr(left?.v) || "-", leftX + 90, cursorY);

    if (right) {
      doc.setFont(undefined, "bold");
      doc.text(`${safeStr(right?.k)}:`, midX, cursorY);
      doc.setFont(undefined, "normal");
      doc.text(safeStr(right?.v) || "-", midX + 110, cursorY);
    }
    cursorY += rowH;
  }
  return cursorY + 8;
};

const addAutoTable = (doc, config, y) => {
  // jsPDF-AutoTable mutates doc with lastAutoTable
  doc.autoTable({
    startY: y,
    margin: { left: 40, right: 40 },
    styles: {
      fontSize: 8.8,
      cellPadding: 4,
      overflow: "linebreak",
      valign: "top",
      textColor: [15, 30, 53],
    },
    headStyles: {
      fillColor: [240, 244, 250],
      textColor: [84, 110, 122],
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: { fillColor: [250, 251, 253] },
    ...config,
  });
  return (doc.lastAutoTable?.finalY || y) + 14;
};

const buildSpecAvgMap = (yearData) => {
  const map = new Map();
  const items = Array.isArray(yearData?.itemwise) ? yearData.itemwise : [];
  items.forEach((row) => {
    const spec = safeStr(row?.specification).trim();
    if (!spec) return;
    const unit = parseMoneyLoose(row?.unit_price);
    const qty = Number(row?.quantity);
    const weight = Number.isFinite(qty) && qty > 0 ? qty : 1;
    const prev = map.get(spec) || { sum: 0, w: 0, count: 0, vendors: new Set() };
    prev.sum += unit * weight;
    prev.w += weight;
    prev.count += 1;
    if (row?.vendor_name) prev.vendors.add(row.vendor_name);
    map.set(spec, prev);
  });
  const out = new Map();
  for (const [spec, v] of map.entries()) {
    const avg = v.w > 0 ? v.sum / v.w : 0;
    out.set(spec, { avg, count: v.count, vendors: [...v.vendors] });
  }
  return out;
};

const buildVendorSpendRows = (itemwise) => {
  const rows = Array.isArray(itemwise) ? itemwise : [];
  const byVendor = new Map();
  let grand = 0;
  for (const r of rows) {
    const vendor = safeStr(r?.vendor_name).trim() || "Unknown";
    const amount = parseMoneyLoose(r?.total_price);
    const prev = byVendor.get(vendor) || { total: 0, poCount: 0, qty: 0 };
    prev.total += amount;
    prev.poCount += 1;
    const q = Number(r?.quantity);
    if (Number.isFinite(q) && q > 0) prev.qty += q;
    byVendor.set(vendor, prev);
    grand += amount;
  }
  const sorted = [...byVendor.entries()].sort((a, b) => b[1].total - a[1].total);
  return sorted.map(([vendor, v]) => {
    const share = grand > 0 ? (v.total / grand) * 100 : 0;
    return [
      vendor,
      Math.round(v.total).toLocaleString("en-IN"),
      `${share.toFixed(1)}%`,
      v.poCount,
      v.qty ? v.qty.toLocaleString("en-IN") : "",
    ];
  });
};

const buildSpecTrendTables = (itemwise) => {
  const rows = Array.isArray(itemwise) ? itemwise : [];
  const monthlyMap = new Map(); // spec|YYYY-MM -> agg
  const yearlyMap = new Map(); // spec|YYYY -> agg

  for (const r of rows) {
    const spec = safeStr(r?.specification).trim();
    if (!spec) continue;
    const unit = parseMoneyLoose(r?.unit_price);
    const qtyRaw = Number(r?.quantity);
    const weight = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;

    const mKey = monthKeyFrom(r?.po_date);
    if (mKey) {
      const k = `${spec}||${mKey}`;
      const agg =
        monthlyMap.get(k) || {
          spec,
          period: mKey,
          label: monthLabel(mKey),
          unitSum: 0,
          wSum: 0,
          qty: 0,
          poCount: 0,
        };
      agg.unitSum += unit * weight;
      agg.wSum += weight;
      agg.qty += Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0;
      agg.poCount += 1;
      monthlyMap.set(k, agg);
    }

    const year = safeStr(r?.po_date).slice(0, 4);
    if (/^\d{4}$/.test(year)) {
      const k = `${spec}||${year}`;
      const agg =
        yearlyMap.get(k) || {
          spec,
          period: year,
          label: year,
          unitSum: 0,
          wSum: 0,
          qty: 0,
          poCount: 0,
        };
      agg.unitSum += unit * weight;
      agg.wSum += weight;
      agg.qty += Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0;
      agg.poCount += 1;
      yearlyMap.set(k, agg);
    }
  }

  const toSortedRows = (map, type) =>
    [...map.values()]
      .map((a) => ({
        spec: a.spec,
        period: a.period,
        label: a.label,
        unitPrice: a.wSum > 0 ? a.unitSum / a.wSum : 0,
        quantity: a.qty,
        poCount: a.poCount,
        type,
      }))
      .sort((a, b) => (a.spec === b.spec ? (a.period > b.period ? 1 : -1) : a.spec > b.spec ? 1 : -1))
      .map((r) => [
        r.spec,
        r.type === "monthly" ? r.period : r.label,
        Math.round(r.unitPrice).toLocaleString("en-IN"),
        Number(r.quantity || 0).toLocaleString("en-IN"),
        r.poCount,
      ]);

  return {
    monthly: toSortedRows(monthlyMap, "monthly"),
    yearly: toSortedRows(yearlyMap, "yearly"),
  };
};

export default function exportSourcingIntelligencePdf({ data }) {
  if (!data) return;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const createdAt = new Date();

  const headerFooter = (hookData) => {
    const pageCount = doc.internal.getNumberOfPages();
    const page = hookData.pageNumber;
    doc.setFontSize(8);
    doc.setTextColor(120, 144, 156);
    doc.text(
      `Generated: ${createdAt.toLocaleString("en-IN")}`,
      40,
      doc.internal.pageSize.getHeight() - 18
    );
    doc.text(
      `Page ${page} of ${pageCount}`,
      pageWidth - 40,
      doc.internal.pageSize.getHeight() - 18,
      { align: "right" }
    );
  };

  // Title
  doc.setFontSize(18);
  doc.setTextColor(13, 71, 161);
  doc.setFont(undefined, "bold");
  doc.text("Sourcing Intelligence — Export", 40, 46);
  doc.setFont(undefined, "normal");

  let y = 66;

  const selected = data?.selected_item || {};
  y = addKeyValues(
    doc,
    [
      { k: "Item", v: safeStr(selected?.item_name) },
      { k: "Item Code", v: safeStr(selected?.item_code) },
      { k: "Organization", v: safeStr(selected?.organization_name) },
      { k: "Years", v: safeStr(Array.isArray(data?.year_wise_analysis) ? data.year_wise_analysis.length : 0) },
    ],
    y
  );

  const years = (Array.isArray(data?.year_wise_analysis) ? data.year_wise_analysis : [])
    .map((v) => v?.year)
    .filter((v) => v !== null && v !== undefined)
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => b - a);

  const yearMap = new Map(
    (Array.isArray(data?.year_wise_analysis) ? data.year_wise_analysis : []).map((v) => [Number(v?.year), v])
  );

  // Overview summary table
  y = sectionTitle(doc, "Overview", y);
  const summaryRows = years.map((year) => {
    const yd = yearMap.get(year) || {};
    const itemwise = Array.isArray(yd?.itemwise) ? yd.itemwise : [];
    const vendors = uniq(itemwise.map((i) => i?.vendor_name));
    const specs = uniq(itemwise.map((i) => i?.specification));
    const delivery = itemwise.reduce(
      (acc, r) => {
        const s = safeStr(r?.delivery_status || "unknown") || "unknown";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      { on_time: 0, overdue: 0, unknown: 0 }
    );
    return [
      year,
      safeStr(yd?.annual_spend?.total_spend),
      safeStr(yd?.annual_spend?.currency),
      itemwise.length,
      vendors.length,
      specs.length,
      delivery.on_time || 0,
      delivery.overdue || 0,
      delivery.unknown || 0,
    ];
  });

  y = addAutoTable(
    doc,
    {
      didDrawPage: headerFooter,
      head: [
        [
          "Year",
          "Annual Spend",
          "Currency",
          "POs",
          "Vendors",
          "Specifications",
          "On Time",
          "Overdue",
          "Unknown",
        ],
      ],
      body: summaryRows,
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 52 },
        1: { cellWidth: 100 },
        2: { cellWidth: 60 },
        3: { cellWidth: 40, halign: "right" },
        4: { cellWidth: 55, halign: "right" },
        5: { cellWidth: 78, halign: "right" },
        6: { cellWidth: 55, halign: "right" },
        7: { cellWidth: 55, halign: "right" },
        8: { cellWidth: 55, halign: "right" },
      },
    },
    y
  );

  // Annual spend trend (chart data)
  y = sectionTitle(doc, "Annual Spend Trend (Chart Data)", y);
  const trendAsc = [...years].slice().sort((a, b) => a - b);
  const trendRows = trendAsc.map((year, idx) => {
    const yd = yearMap.get(year) || {};
    const spend = parseMoneyLoose(yd?.annual_spend?.total_spend);
    const prevYear = idx > 0 ? trendAsc[idx - 1] : null;
    const prev = prevYear ? parseMoneyLoose(yearMap.get(prevYear)?.annual_spend?.total_spend) : 0;
    const yoy = prev > 0 ? ((spend - prev) / prev) * 100 : null;
    return [
      year,
      Math.round(spend).toLocaleString("en-IN"),
      safeStr(yd?.annual_spend?.currency),
      prevYear ? Math.round(prev).toLocaleString("en-IN") : "-",
      prevYear ? (yoy !== null ? `${yoy >= 0 ? "+" : ""}${yoy.toFixed(2)}%` : "-") : "-",
    ];
  });
  y = addAutoTable(
    doc,
    {
      didDrawPage: headerFooter,
      head: [["Year", "Spend (₹)", "Currency", "Prev Year Spend (₹)", "YoY %"]],
      body: trendRows.length ? trendRows : [["-", "-", "-", "-", "-"]],
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 110, halign: "right" },
        2: { cellWidth: 70 },
        3: { cellWidth: 130, halign: "right" },
        4: { cellWidth: 70, halign: "right" },
      },
    },
    y
  );

  // Same-spec comparison for adjacent years (derived from response)
  y = sectionTitle(doc, "Same-Specification Price Comparison (Adjacent Years)", y);
  if (years.length < 2) {
    doc.setFontSize(10);
    doc.setTextColor(84, 110, 122);
    doc.text("Not enough years to compare.", 40, y);
    y += 18;
  } else {
    const rows = [];
    for (let i = 0; i < years.length - 1; i += 1) {
      const currYear = years[i];
      const prevYear = years[i + 1];
      const curr = yearMap.get(currYear);
      const prev = yearMap.get(prevYear);
      if (!curr || !prev) continue;

      const currMap = buildSpecAvgMap(curr);
      const prevMap = buildSpecAvgMap(prev);

      for (const [spec, prevAgg] of prevMap.entries()) {
        const currAgg = currMap.get(spec);
        if (!currAgg) continue;
        const change = currAgg.avg - prevAgg.avg;
        const pct = prevAgg.avg > 0 ? (change / prevAgg.avg) * 100 : 0;
        rows.push([
          `${prevYear} → ${currYear}`,
          spec,
          `${Math.round(prevAgg.avg).toLocaleString("en-IN")}`,
          `${Math.round(currAgg.avg).toLocaleString("en-IN")}`,
          `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`,
          `${change >= 0 ? "+" : ""}${Math.round(change).toLocaleString("en-IN")}`,
          uniq(prevAgg.vendors).join(", "),
          uniq(currAgg.vendors).join(", "),
        ]);
      }
    }

    if (rows.length === 0) {
      doc.setFontSize(10);
      doc.setTextColor(84, 110, 122);
      doc.text("No overlapping specifications found between adjacent years.", 40, y);
      y += 18;
    } else {
      y = addAutoTable(
        doc,
        {
          didDrawPage: headerFooter,
          head: [["Years", "Specification", "Prev Avg (₹)", "Curr Avg (₹)", "Change %", "Diff (₹)", "Vendors (Prev)", "Vendors (Curr)"]],
          body: rows,
          columnStyles: {
            0: { cellWidth: 78 },
            1: { cellWidth: 270 },
            2: { cellWidth: 76, halign: "right" },
            3: { cellWidth: 76, halign: "right" },
            4: { cellWidth: 62, halign: "right" },
            5: { cellWidth: 70, halign: "right" },
            6: { cellWidth: 150 },
            7: { cellWidth: 150 },
          },
        },
        y
      );
    }
  }

  // Full data per year
  for (const year of years) {
    const yd = yearMap.get(year) || {};
    const itemwise = Array.isArray(yd?.itemwise) ? yd.itemwise : [];
    const monthly = Array.isArray(yd?.monthly_spend) ? yd.monthly_spend : [];

    y = sectionTitle(doc, `Year: FY ${year}`, y);
    y = addKeyValues(
      doc,
      [
        { k: "Annual Spend", v: safeStr(yd?.annual_spend?.total_spend) },
        { k: "Currency", v: safeStr(yd?.annual_spend?.currency) },
        { k: "PO Records", v: safeStr(itemwise.length) },
        { k: "Unique Vendors", v: safeStr(uniq(itemwise.map((r) => r?.vendor_name)).length) },
      ],
      y
    );

    y = sectionTitle(doc, "Monthly Spend", y);
    const monthlyRows = monthly.map((m) => [
      safeStr(m?.month),
      safeStr(m?.month_number),
      safeStr(m?.total_spend),
      safeStr(m?.purchase_count),
    ]);
    y = addAutoTable(
      doc,
      {
        didDrawPage: headerFooter,
        head: [["Month", "Month #", "Total Spend", "Purchase Count"]],
        body: monthlyRows.length ? monthlyRows : [["-", "-", "-", "-"]],
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: 60, halign: "right" },
          2: { cellWidth: 100, halign: "right" },
          3: { cellWidth: 90, halign: "right" },
        },
      },
      y
    );

    y = sectionTitle(doc, "Supplier-wise Spend (Chart Data)", y);
    const vendorRows = buildVendorSpendRows(itemwise);
    y = addAutoTable(
      doc,
      {
        didDrawPage: headerFooter,
        head: [["Supplier", "Total Spend (₹)", "Share", "PO Count", "Total Quantity"]],
        body: vendorRows.length ? vendorRows : [["-", "-", "-", "-", "-"]],
        columnStyles: {
          0: { cellWidth: 260 },
          1: { cellWidth: 110, halign: "right" },
          2: { cellWidth: 70, halign: "right" },
          3: { cellWidth: 70, halign: "right" },
          4: { cellWidth: 90, halign: "right" },
        },
      },
      y
    );

    // Spec unit-price & quantity trend (table form of UI charts)
    const specTrends = buildSpecTrendTables(itemwise);
    y = sectionTitle(doc, "Unit Price & Quantity Trends (by Specification) — Monthly (Chart Data)", y);
    y = addAutoTable(
      doc,
      {
        didDrawPage: headerFooter,
        head: [["Specification", "Month (YYYY-MM)", "Avg Unit Price (₹)", "Total Quantity", "PO Events"]],
        body: specTrends.monthly.length ? specTrends.monthly : [["-", "-", "-", "-", "-"]],
        styles: { fontSize: 8.2, cellPadding: 3.6, overflow: "linebreak", valign: "top" },
        columnStyles: {
          0: { cellWidth: 420 },
          1: { cellWidth: 92 },
          2: { cellWidth: 110, halign: "right" },
          3: { cellWidth: 80, halign: "right" },
          4: { cellWidth: 76, halign: "right" },
        },
        pageBreak: "auto",
      },
      y
    );

    y = sectionTitle(doc, "Unit Price & Quantity Trends (by Specification) — Yearly (Chart Data)", y);
    y = addAutoTable(
      doc,
      {
        didDrawPage: headerFooter,
        head: [["Specification", "Year", "Avg Unit Price (₹)", "Total Quantity", "PO Events"]],
        body: specTrends.yearly.length ? specTrends.yearly : [["-", "-", "-", "-", "-"]],
        styles: { fontSize: 8.2, cellPadding: 3.6, overflow: "linebreak", valign: "top" },
        columnStyles: {
          0: { cellWidth: 420 },
          1: { cellWidth: 72 },
          2: { cellWidth: 110, halign: "right" },
          3: { cellWidth: 80, halign: "right" },
          4: { cellWidth: 76, halign: "right" },
        },
        pageBreak: "auto",
      },
      y
    );

    y = sectionTitle(doc, "PO Details (All Fields)", y);
    const detailRows = itemwise.map((r, idx) => {
      const comp = r?.last_purchase_comparison || {};
      return [
        idx + 1,
        safeStr(r?.item_name),
        safeStr(r?.item_code),
        safeStr(r?.specification),
        safeStr(r?.description),
        safeStr(r?.vendor_name),
        safeStr(r?.po_number),
        formatDateMaybe(r?.po_date),
        formatDateMaybe(r?.expected_delivery_date),
        safeStr(r?.remaining_delivery_days),
        safeStr(r?.delivery_status),
        safeStr(r?.quantity),
        safeStr(r?.unit_price),
        safeStr(r?.total_price),
        safeStr(r?.currency),
        safeStr(comp?.comparison_status),
        safeStr(comp?.previous_po_number),
        formatDateMaybe(comp?.previous_po_date),
        safeStr(comp?.previous_unit_price),
        safeStr(comp?.previous_total_price),
        safeStr(comp?.unit_price_difference),
        safeStr(comp?.unit_price_difference_percentage),
        safeStr(comp?.price_direction),
      ];
    });

    y = addAutoTable(
      doc,
      {
        didDrawPage: headerFooter,
        head: [
          [
            "#",
            "Item",
            "Item Code",
            "Specification",
            "Description",
            "Supplier",
            "PO #",
            "PO Date",
            "Expected Delivery",
            "Remaining Days",
            "Delivery Status",
            "Quantity",
            "Unit Price",
            "Total Price",
            "Currency",
            "Comparison Status",
            "Prev PO #",
            "Prev PO Date",
            "Prev Unit Price",
            "Prev Total Price",
            "Unit Diff",
            "Unit Diff %",
            "Price Direction",
          ],
        ],
        body: detailRows.length ? detailRows : [["-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-"]],
        styles: { fontSize: 7.6, cellPadding: 3.2, overflow: "linebreak", valign: "top" },
        columnStyles: {
          0: { cellWidth: 22, halign: "right" },
          1: { cellWidth: 90 },
          2: { cellWidth: 55 },
          3: { cellWidth: 180 },
          4: { cellWidth: 180 },
          5: { cellWidth: 110 },
          6: { cellWidth: 55 },
          7: { cellWidth: 60 },
          8: { cellWidth: 72 },
          9: { cellWidth: 54, halign: "right" },
          10: { cellWidth: 70 },
          11: { cellWidth: 34, halign: "right" },
          12: { cellWidth: 58, halign: "right" },
          13: { cellWidth: 62, halign: "right" },
          14: { cellWidth: 44 },
          15: { cellWidth: 130 },
          16: { cellWidth: 50 },
          17: { cellWidth: 62 },
          18: { cellWidth: 62, halign: "right" },
          19: { cellWidth: 72, halign: "right" },
          20: { cellWidth: 55, halign: "right" },
          21: { cellWidth: 60, halign: "right" },
          22: { cellWidth: 64 },
        },
        pageBreak: "auto",
      },
      y
    );
  }

  const itemSafe = safeStr(selected?.item_name).replace(/[\\/:*?"<>|]+/g, "-").trim() || "Item";
  const dateSafe = createdAt.toISOString().slice(0, 10);
  doc.save(`Sourcing Intelligence - ${itemSafe} - ${dateSafe}.pdf`);
}
